import { forwardRef, PropsWithChildren } from 'react';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Slide,
  SlideProps,
  Stack,
  SxProps,
  Theme,
  Typography,
} from '@mui/material';

import palette from '@/theme/palette.ts';

type SlideInModalDialogStyledProps = PropsWithChildren<{
  title: string;
  open: boolean;
  setOpen: (open: boolean) => void;
  onClose?: () => void;
  onActionButtonClick?: () => void;
  onSecondaryActionButtonClick?: () => void;
  actionButtonLabel?: string;
  secondaryActionButtonLabel?: string;
  isCorrectlyFilledOut?: boolean;
  sx?: SxProps<Theme>;
}>;

const Transition = forwardRef<HTMLElement, PropsWithChildren<SlideProps>>((props, ref) => {
  return (
    <Slide direction="left" ref={ref} {...props} timeout={400}>
      {props.children}
    </Slide>
  );
});

export const SlideInModalDialogStyled = ({
  title,
  open,
  setOpen,
  onClose,
  onActionButtonClick,
  onSecondaryActionButtonClick,
  actionButtonLabel,
  secondaryActionButtonLabel,
  isCorrectlyFilledOut = true,
  children,
  sx,
}: SlideInModalDialogStyledProps) => {
  const handleClose =
    onClose ||
    (() => {
      setOpen(false);
    });
  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      TransitionComponent={Transition}
      sx={{
        '.MuiPaper-root': { borderRadius: '10px' },
        '.MuiPaper-root:first-child': {
          height: '100dvh',
          overflowY: 'scroll',
          position: 'fixed',
          right: 0,
          top: 0,
          maxWidth: '840px',
        },
        ...sx,
      }}
    >
      <DialogTitle>
        <Box sx={{ borderBottom: `1px solid ${palette.border.default}`, pb: 2 }}>{title}</Box>
      </DialogTitle>
      <IconButton
        aria-label="close"
        onClick={handleClose}
        sx={{ position: 'absolute', right: 8, top: 12 }}
      >
        <span className="material-symbols-rounded">close</span>
      </IconButton>
      {!isCorrectlyFilledOut && (
        <Stack
          direction="row"
          justifyContent="flex-start"
          alignItems="center"
          sx={{
            marginX: 3,
            padding: '16px',
            gap: '10px',
            borderRadius: '4px',
            background: palette.toast.error,
          }}
        >
          <ErrorOutlineIcon fontSize="inherit" sx={{ color: palette.surface.white }} />
          <Typography variant="body2" sx={{ color: palette.text.contrast }}>
            Vyplňte všechny potřebné údaje
          </Typography>
        </Stack>
      )}
      {children && <DialogContent>{children}</DialogContent>}

      {children && <Box sx={{ borderTop: `1px solid ${palette.border.default}`, mx: 3 }} />}
      <DialogActions>
        <Stack
          direction="row"
          justifyContent={onSecondaryActionButtonClick ? 'space-between' : 'end'}
          width="100%"
          p={3}
          gap={2}
        >
          {onSecondaryActionButtonClick && (
            <Button
              variant="text"
              sx={{ color: palette.primary.main }}
              onClick={onSecondaryActionButtonClick}
            >
              {secondaryActionButtonLabel || 'Smazat'}
            </Button>
          )}
          {onActionButtonClick && (
            <Button
              variant="contained"
              onClick={onActionButtonClick}
              sx={{ alignSelf: 'flex-end' }}
            >
              {actionButtonLabel || 'Přidat'}
            </Button>
          )}
        </Stack>
      </DialogActions>
    </Dialog>
  );
};
